import type { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'

import { FULL_YEAR_QUARTER } from '../../../../lib/revenueAndSob/constants'
import { isActualsFinSobRow } from '../../../../lib/revenueAndSob/isActualsFinSobRow'
import type { IPartnerFinSobRow } from '../../../../model/types'

/** A quarter of the year being collected: its figure, a gap the status is about, or not yet due. */
export type TSobGridCell = { kind: 'value'; sob: number } | { kind: 'missing' } | { kind: 'future' }

export interface ISobQuarterGrid {
  /** The quarters the grid reads — `1`–`4`, or `[FULL_YEAR_QUARTER]` where the year is read whole. */
  quarters: number[]
  /** Last year's SOB per quarter; `null` where no figure came in. */
  lastYear: (number | null)[]
  thisYear: TSobGridCell[]
  /** The plan's SOB for the year. */
  plan: number | null
}

interface IParams {
  rows: IPartnerFinSobRow[]
  offering: ProgramTypes
  category: string
  fYear: number
  /** The quarter SOB is collected for; `null` reads the whole year. */
  fQuarter: number | null
}

const QUARTERS = [1, 2, 3, 4]

const isSob = (value: number | null): value is number =>
  typeof value === 'number' && Number.isFinite(value)

/**
 * The SOB a category of the offering reports, quarter by quarter, this year and last — what the
 * `SOB Actuals are missing` table shows beside each category.
 *
 * The category's own line is read first: its sub-category lines carry figures of their own and
 * would otherwise stand for the category. A quarter after the one being collected is not due yet,
 * so it is left empty rather than called missing; one up to it with no figure is the gap.
 */
export const getSobQuarterGrid = ({
  rows,
  offering,
  category,
  fYear,
  fQuarter,
}: IParams): ISobQuarterGrid => {
  const offeringName = offering.trim()
  const categoryRows = rows.filter(
    (row) => row.offering?.trim() === offeringName && row.category === category
  )

  const findSob = (year: number, quarter: number, isPlan: boolean) => {
    const matching = categoryRows.filter(
      (row) =>
        row.fYear === year &&
        row.fQuarter === quarter &&
        (isPlan ? row.isTarget : isActualsFinSobRow(row)) &&
        isSob(row.sob)
    )

    return (matching.find((row) => !row.subCategory) ?? matching[0])?.sob ?? null
  }

  const quarters = fQuarter === null ? [FULL_YEAR_QUARTER] : QUARTERS

  return {
    quarters,
    lastYear: quarters.map((quarter) => findSob(fYear - 1, quarter, false)),
    thisYear: quarters.map((quarter) => {
      const sob = findSob(fYear, quarter, false)

      if (sob !== null) {
        return { kind: 'value', sob }
      }

      return fQuarter !== null && quarter > fQuarter ? { kind: 'future' } : { kind: 'missing' }
    }),
    plan: findSob(fYear, FULL_YEAR_QUARTER, true),
  }
}
